import { useMemo } from "react";
import clsx from "clsx";
import type { CSSProperties } from "react";
import {
  buildDailyTotals,
  formatCalendarHours,
  formatExpectedHours,
  toIsoDateLocal,
  type HeroUsageDayTotals,
  type HeroUsageRow,
} from "@/lib/auswertungen";

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] as const;

const EMPTY_TOTALS: HeroUsageDayTotals = {
  writtenSeconds: 0,
  timesheetSeconds: 0,
  festpreisSeconds: 0,
};

interface CalendarDay {
  isoDate: string;
  outsideMonth: boolean;
}

interface CalendarMonth {
  key: string;
  label: string;
  days: CalendarDay[];
}

function buildMonths(fromIso: string, toIso: string): CalendarMonth[] {
  const from = new Date(`${fromIso}T00:00:00`);
  const to = new Date(`${toIso}T00:00:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || from > to) return [];

  const months: CalendarMonth[] = [];
  const cursor = new Date(from.getFullYear(), from.getMonth(), 1);

  while (cursor <= to) {
    const monthStart = new Date(cursor);
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const visibleStart = from > monthStart ? new Date(from) : monthStart;
    const visibleEnd = to < monthEnd ? new Date(to) : monthEnd;

    if (visibleStart <= visibleEnd) {
      const startWeekday = (visibleStart.getDay() + 6) % 7;
      const endWeekday = (visibleEnd.getDay() + 6) % 7;
      const days: CalendarDay[] = [];

      for (let offset = startWeekday; offset > 0; offset -= 1) {
        const outside = new Date(visibleStart);
        outside.setDate(visibleStart.getDate() - offset);
        days.push({ isoDate: toIsoDateLocal(outside), outsideMonth: true });
      }

      const dayCursor = new Date(visibleStart);
      while (dayCursor <= visibleEnd) {
        days.push({ isoDate: toIsoDateLocal(dayCursor), outsideMonth: false });
        dayCursor.setDate(dayCursor.getDate() + 1);
      }

      for (let offset = 1; offset <= 6 - endWeekday; offset += 1) {
        const outside = new Date(visibleEnd);
        outside.setDate(visibleEnd.getDate() + offset);
        days.push({ isoDate: toIsoDateLocal(outside), outsideMonth: true });
      }

      months.push({
        key: `${cursor.getFullYear()}-${cursor.getMonth() + 1}`,
        label: cursor.toLocaleDateString("de-DE", { month: "long", year: "numeric" }),
        days,
      });
    }

    cursor.setMonth(cursor.getMonth() + 1);
  }

  return months;
}

function UsageRing({ writtenSeconds, assignedSeconds }: { writtenSeconds: number; assignedSeconds: number }) {
  const percentage = writtenSeconds > 0 ? (assignedSeconds / writtenSeconds) * 100 : 0;
  const clamped = Math.max(0, Math.min(percentage, 100));

  return (
    <span
      className={clsx(
        "eh-hours-usage-cell eh-report-calendar-day__usage",
        percentage > 100 && "eh-hours-usage-cell--over",
      )}
    >
      <span
        className="eh-hours-usage-ring"
        style={{ "--eh-hours-p": clamped.toFixed(2) } as CSSProperties}
        aria-hidden
      >
        <span className="eh-hours-usage-label">{Math.round(percentage)}%</span>
      </span>
    </span>
  );
}

export function UsageCalendar({
  rows,
  from,
  to,
  onSelectDay,
  hideMonthTitle = false,
  expectedHoursByDay,
}: {
  rows: HeroUsageRow[];
  from: string;
  to: string;
  onSelectDay: (isoDate: string) => void;
  hideMonthTitle?: boolean;
  expectedHoursByDay?: Map<string, number> | null;
}) {
  const dailyTotals = useMemo(() => buildDailyTotals(rows), [rows]);
  const months = useMemo(() => buildMonths(from, to), [from, to]);

  if (months.length === 0) {
    return <p className="text-sm text-label-secondary">Kein gültiger Zeitraum ausgewählt.</p>;
  }

  return (
    <div>
      {months.map((month) => (
        <section key={month.key} className="eh-report-calendar-month">
          {!hideMonthTitle ? (
            <h3 className="eh-report-calendar-month__title">{month.label}</h3>
          ) : null}
          <div className="eh-report-calendar-weekdays">
            {WEEKDAYS.map((weekday) => (
              <div key={weekday} className="eh-report-calendar-weekdays__cell">
                {weekday}
              </div>
            ))}
          </div>
          <div className="eh-report-calendar-grid">
            {month.days.map(({ isoDate, outsideMonth }) => {
              const totals = dailyTotals.get(isoDate) ?? EMPTY_TOTALS;
              const dayNumber = Number(isoDate.slice(8, 10));
              const expectedHours =
                expectedHoursByDay == null
                  ? undefined
                  : (expectedHoursByDay.get(isoDate) ?? 0);
              return (
                <button
                  key={isoDate}
                  type="button"
                  disabled={outsideMonth}
                  onClick={() => onSelectDay(isoDate)}
                  className={clsx(
                    "eh-report-calendar-day",
                    outsideMonth
                      ? "eh-report-calendar-day--outside"
                      : "eh-report-calendar-day--interactive",
                  )}
                >
                  <span className="eh-report-calendar-day__date">{dayNumber}.</span>
                  {!outsideMonth && (
                    <>
                      <UsageRing
                        writtenSeconds={totals.writtenSeconds}
                        assignedSeconds={totals.timesheetSeconds + totals.festpreisSeconds}
                      />
                      <div className="eh-report-calendar-day__meta eh-report-calendar-day__meta--soll">
                        Soll: {formatExpectedHours(expectedHours)}
                      </div>
                      <div className="eh-report-calendar-day__meta">
                        Geschrieben: {formatCalendarHours(totals.writtenSeconds)}
                      </div>
                      <div className="eh-report-calendar-day__meta">
                        Auf Nachweis: {formatCalendarHours(totals.timesheetSeconds)}
                      </div>
                      <div className="eh-report-calendar-day__meta eh-report-calendar-day__meta--festpreis">
                        Festpreis: {formatCalendarHours(totals.festpreisSeconds)}
                      </div>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
